import React from 'react'
import {
  ArrowDown,
  ArrowRight,
  Check,
  Chevron,
  ChevronButton,
  ChevronDouble,
  Close,
  Facebook,
  FilmInIcelandLogo,
  FilmInIcelandLogoMobile,
  Filter,
  EiderdownLogo,
  GraenvangurLogo,
  GreenByIcelandLogo,
  GreenByIcelandLogoMobile,
  HeimstorgLogo,
  HorsesOfIcelandLogo,
  HorsesOfIcelandLogoMobile,
  InspiredByIcelandLogoHorizontal,
  InspiredByIcelandLogoStacked,
  Instagram,
  IslandsstofaLogoColor,
  IslandsstofaLogoMini,
  LinkedIn,
  Message,
  ModalArrow,
  ModalArrowSmall,
  Play,
  Search,
  Twitter,
  Youtube,
  FilmTrailer,
  IcelandicLogo,
  ExternalLink,
  VisitIcelandLogo,
  Plus,
  Minus,
  LargeArrow,
  Calendar,
  FilePdf,
  CloudDownload,
  Telephone,
  RoadSign,
  Clock,
  Reading,
  Pen,
  StudyLogo,
  Binoculars,
  People,
  ClockLarge,
  CalendarLarge,
  DataCentersLogo,
  SmallEx,
  Mail,
  Location,
  Globe,
  CheckmarkCircle,
  RecordInIcelandLogo,
  BusinessIcelandLogo,
  BusinessIcelandVerticalLogo,
  WorkInIcelandLogo,
  MeetInReykjavikLogo,
  CleanAndSafe,
  Vakinn,
  SustainabilityCertification,
  CleanAndSafeLarge,
  LongArrow,
  SmallPlay,
  CalendarBasic
} from './SVGS'

type Props = {
  type: IconType
  containerClass?: string
  color?: string
}

const iconTypes = {
  arrowDown: 1,
  arrowRight: 2,
  check: 3,
  close: 4,
  facebook: 5,
  filmInIcelandLogo: 6,
  filmInIcelandLogoMobile: 7,
  filter: 8,
  eiderdownLogo: 9,
  graenvangurLogo: 10,
  greenByIcelandLogo: 11,
  greenByIcelandLogoMobile: 12,
  heimstorgLogo: 13,
  horsesOfIcelandLogo: 14,
  horsesOfIcelandLogoMobile: 15,
  inspiredByIcelandLogoHorizontal: 16,
  inspiredByIcelandLogoStacked: 17,
  instagram: 18,
  islandsstofaLogoColor: 19,
  islandsstofaLogoMini: 20,
  linkedIn: 21,
  modalArrow: 22,
  modalArrowSmall: 23,
  play: 24,
  search: 25,
  twitter: 26,
  youtube: 27,
  filmTrailer: 28,
  icelandicLogo: 29,
  externalLink: 30,
  visitIcelandLogo: 31,
  plus: 32,
  minus: 33,
  largeArrow: 34,
  calendar: 35,
  filePdf: 36,
  cloudDownload: 37,
  telephone: 38,
  roadSign: 39,
  clock: 40,
  reading: 41,
  pen: 42,
  studyLogo: 43,
  binoculars: 44,
  people: 45,
  clockLarge: 46,
  calendarLarge: 47,
  dataCentersLogo: 48,
  smallEx: 49,
  location: 50,
  globe: 51,
  mail: 52,
  checkmarkCircle: 53,
  recordInIcelandLogo: 54,
  businessIcelandLogo: 55,
  workInIcelandLogo: 56,
  meetInReykjavikLogo: 57,
  cleanAndSafe: 58,
  vakinn: 59,
  cleanAndSafeLarge: 60,
  chevronButton: 61,
  chevron: 62,
  sustainabilityCertification: 63,
  businessIcelandVerticalLogo: 64,
  chevronDouble: 65,
  message: 66,
  longArrow: 67,
  smallPlay: 68,
  calendarBasic: 69
} as const

export type IconType = keyof typeof iconTypes

export const isIconType = (t: string): t is IconType => Object.keys(iconTypes).includes(t)

const Icon: React.FC<Props & React.HTMLAttributes<HTMLElement>> = ({ type, containerClass = '', ...rest }) => {
  const svg: { [key in IconType]: React.ElementType } = {
    arrowDown: ArrowDown,
    arrowRight: ArrowRight,
    check: Check,
    chevron: Chevron,
    chevronButton: ChevronButton,
    chevronDouble: ChevronDouble,
    close: Close,
    facebook: Facebook,
    filmInIcelandLogo: FilmInIcelandLogo,
    filmInIcelandLogoMobile: FilmInIcelandLogoMobile,
    filter: Filter,
    eiderdownLogo: EiderdownLogo,
    graenvangurLogo: GraenvangurLogo,
    greenByIcelandLogo: GreenByIcelandLogo,
    greenByIcelandLogoMobile: GreenByIcelandLogoMobile,
    heimstorgLogo: HeimstorgLogo,
    horsesOfIcelandLogo: HorsesOfIcelandLogo,
    horsesOfIcelandLogoMobile: HorsesOfIcelandLogoMobile,
    inspiredByIcelandLogoHorizontal: InspiredByIcelandLogoHorizontal,
    inspiredByIcelandLogoStacked: InspiredByIcelandLogoStacked,
    instagram: Instagram,
    islandsstofaLogoColor: IslandsstofaLogoColor,
    islandsstofaLogoMini: IslandsstofaLogoMini,
    linkedIn: LinkedIn,
    message: Message,
    modalArrow: ModalArrow,
    modalArrowSmall: ModalArrowSmall,
    play: Play,
    search: Search,
    twitter: Twitter,
    youtube: Youtube,
    filmTrailer: FilmTrailer,
    icelandicLogo: IcelandicLogo,
    externalLink: ExternalLink,
    visitIcelandLogo: VisitIcelandLogo,
    plus: Plus,
    minus: Minus,
    largeArrow: LargeArrow,
    calendar: Calendar,
    calendarBasic: CalendarBasic,
    filePdf: FilePdf,
    cloudDownload: CloudDownload,
    telephone: Telephone,
    roadSign: RoadSign,
    clock: Clock,
    reading: Reading,
    pen: Pen,
    studyLogo: StudyLogo,
    binoculars: Binoculars,
    people: People,
    clockLarge: ClockLarge,
    calendarLarge: CalendarLarge,
    dataCentersLogo: DataCentersLogo,
    smallEx: SmallEx,
    location: Location,
    checkmarkCircle: CheckmarkCircle,
    mail: Mail,
    globe: Globe,
    recordInIcelandLogo: RecordInIcelandLogo,
    businessIcelandLogo: BusinessIcelandLogo,
    workInIcelandLogo: WorkInIcelandLogo,
    meetInReykjavikLogo: MeetInReykjavikLogo,
    cleanAndSafe: CleanAndSafe,
    vakinn: Vakinn,
    cleanAndSafeLarge: CleanAndSafeLarge,
    sustainabilityCertification: SustainabilityCertification,
    businessIcelandVerticalLogo: BusinessIcelandVerticalLogo,
    longArrow: LongArrow,
    smallPlay: SmallPlay
  }

  const Cmp = svg[type]

  return (
    <div className={containerClass} {...rest}>
      <Cmp className='icon' />
    </div>
  )
}

export default Icon
